import { useState, useEffect, useCallback, useRef } from 'react'
import { Link } from 'react-router-dom'
import { format } from 'date-fns'
import { PageContainer } from '@/components/layout'
import { Button, Card, TextArea, Tabs, Spinner, Modal, Badge, RadioGroup, ToastContainer, useToast } from '@/components/ui'
import { PaywallModal } from '@/components/PaywallModal'
import { ScheduleModal } from '@/components/ScheduleModal'
import { api, ApiError } from '@/services/api'
import { getErrorMessage } from '@/services/errors'
import { useGenerationJob } from '@/hooks/useGenerationJob'
import { useSubscription } from '@/context/SubscriptionContext'
import { ErrorCode } from '@/services/types'
import type { ContentQueueItem, QueueStatus, ToneOverride, QueueItemAccount } from '@/services/types'
import {
  getCharLimit,
  getItemPlatform,
  getPlatformDisplayName,
  getReplyUrl,
  getReplyToPlatformId,
  getPlatformConfig,
  getItemPostUrl,
} from '@/services/platforms'
import { MediaUpload } from '@/components/MediaUpload'

const CONTENT_TYPE_LABELS: Record<string, string> = {
  single_tweet: 'Single Post',
  thread: 'Thread',
  quote_reply: 'Quote Reply',
  instagram_caption: 'Caption',
}

const TONE_LABELS: Record<string, string> = {
  casual: 'Casual',
  professional: 'Professional',
  humorous: 'Humorous',
  inspirational: 'Inspirational',
  educational: 'Educational',
  provocative: 'Provocative',
}

const TONE_OPTIONS = [
  { value: 'casual', label: 'Casual' },
  { value: 'professional', label: 'Professional' },
  { value: 'humorous', label: 'Humorous' },
  { value: 'inspirational', label: 'Inspirational' },
  { value: 'educational', label: 'Educational' },
  { value: 'provocative', label: 'Provocative' },
]

function AccountIndicator({ account }: { account?: QueueItemAccount | null }) {
  if (!account) return null
  return (
    <div className="flex items-center gap-1.5 mb-2">
      {account.profile_image_url && (
        <img
          src={account.profile_image_url}
          alt={account.username}
          className="w-5 h-5 rounded-full"
        />
      )}
      <span className="text-xs text-secondary-text">@{account.username}</span>
    </div>
  )
}

const statusTabs = [
  { id: 'drafts', label: 'Drafts' },
  { id: 'approved', label: 'Approved' },
  { id: 'scheduled', label: 'Scheduled' },
  { id: 'posted', label: 'Posted' },
]

export function QueuePage() {
  const { toasts, addToast, dismissToast } = useToast()
  const { status: jobStatus, isGenerating: jobIsGenerating, startJob, clearJob } = useGenerationJob()
  const { refreshUsage } = useSubscription()
  const [activeTab, setActiveTab] = useState('drafts')
  const [drafts, setDrafts] = useState<ContentQueueItem[]>([])
  const [approved, setApproved] = useState<ContentQueueItem[]>([])
  const [scheduled, setScheduled] = useState<ContentQueueItem[]>([])
  const [posted, setPosted] = useState<ContentQueueItem[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [schedulingItem, setSchedulingItem] = useState<ContentQueueItem | null>(null)
  const [isScheduling, setIsScheduling] = useState(false)
  const [isReschedule, setIsReschedule] = useState(false)
  const [editingItem, setEditingItem] = useState<ContentQueueItem | null>(null)
  const [editedContent, setEditedContent] = useState('')
  const [editMediaUrls, setEditMediaUrls] = useState<string[]>([])
  const [editMediaError, setEditMediaError] = useState<string | null>(null)
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null)
  const [remixingItem, setRemixingItem] = useState<ContentQueueItem | null>(null)
  const [remixPrompt, setRemixPrompt] = useState('')
  const [remixTone, setRemixTone] = useState<ToneOverride | null>(null)
  const [isRemixing, setIsRemixing] = useState(false)
  const [showPaywall, setShowPaywall] = useState(false)
  const isRemixJobRef = useRef(false)

  const loadQueue = useCallback(async () => {
    try {
      const data = await api.getQueue()
      setDrafts(data.drafts)
      setApproved(data.approved)
      setScheduled(data.scheduled ?? [])
      setPosted(data.posted)
    } catch (err) {
      addToast(err instanceof ApiError ? getErrorMessage(err, 'Failed to load queue') : 'Failed to load queue', 'error')
    } finally {
      setIsLoading(false)
    }
  }, [addToast])

  useEffect(() => {
    loadQueue()
  }, [loadQueue])

  // Refresh queue when generation job completes
  useEffect(() => {
    if (jobStatus === 'completed') {
      loadQueue()
      if (isRemixJobRef.current) {
        addToast('Remix complete! Your new draft is ready.', 'success', {
          duration: 60000,
          action: { label: 'View Drafts', onClick: () => setActiveTab('drafts') },
        })
        isRemixJobRef.current = false
      }
      clearJob()
    } else if (jobStatus === 'failed') {
      if (isRemixJobRef.current) {
        addToast('Remix failed. Please try again.', 'error')
        isRemixJobRef.current = false
      }
      clearJob()
    }
  }, [jobStatus, loadQueue, clearJob, addToast])

  const handleUpdateStatus = async (item: ContentQueueItem, newStatus: QueueStatus) => {
    try {
      await api.updateQueueItem(item.id, { status: newStatus })
      addToast(`Item ${newStatus === 'approved' ? 'approved' : 'moved to drafts'}`, 'success')
      loadQueue()
    } catch (err) {
      addToast(err instanceof ApiError ? getErrorMessage(err, 'Failed to update status') : 'Failed to update status', 'error')
    }
  }

  const handlePost = async (item: ContentQueueItem) => {
    const platform = getItemPlatform(item)
    const platformName = getPlatformDisplayName(platform)
    try {
      const response = await api.postToPlatform(item.id)
      addToast(`Posted to ${platformName}!`, 'success')
      if (response.post_url) {
        window.open(response.post_url, '_blank')
      }
      loadQueue()
    } catch (err) {
      if (err instanceof ApiError && err.code === ErrorCode.INSTAGRAM_MEDIA_REQUIRED) {
        addToast('Instagram posts require at least one image. Edit the post to add an image.', 'error')
      } else {
        addToast(err instanceof ApiError ? getErrorMessage(err, 'Failed to post') : 'Failed to post', 'error')
      }
    }
  }

  const handleDelete = async (id: number) => {
    try {
      await api.deleteQueueItem(id)
      addToast('Item deleted', 'success')
      setDeleteConfirmId(null)
      loadQueue()
    } catch (err) {
      addToast(err instanceof ApiError ? getErrorMessage(err, 'Failed to delete') : 'Failed to delete', 'error')
    }
  }

  const handleSaveEdit = async () => {
    if (!editingItem) return

    try {
      const updateData: { content_text: string; media_urls?: string[] } = {
        content_text: editedContent,
      }
      const platform = getItemPlatform(editingItem)
      const config = getPlatformConfig(platform)
      if (config.supportsMedia) {
        updateData.media_urls = editMediaUrls
      }
      await api.updateQueueItem(editingItem.id, updateData)
      addToast('Content updated', 'success')
      setEditingItem(null)
      loadQueue()
    } catch (err) {
      addToast(err instanceof ApiError ? getErrorMessage(err, 'Failed to update') : 'Failed to update', 'error')
    }
  }

  const openEditModal = (item: ContentQueueItem) => {
    setEditingItem(item)
    setEditedContent(item.content_text)
    setEditMediaUrls(item.media_urls ?? [])
    setEditMediaError(null)
  }

  const openRemixModal = (item: ContentQueueItem) => {
    setRemixingItem(item)
    setRemixPrompt(item.generation_params?.prompt ?? '')
    setRemixTone(item.generation_params?.tone_override ?? null)
  }

  const handleRemix = async () => {
    if (!remixingItem) return

    const freshUsage = await refreshUsage()
    if (freshUsage && !freshUsage.is_unlimited && freshUsage.generations_remaining <= 0) {
      setRemixingItem(null)
      setShowPaywall(true)
      return
    }

    setIsRemixing(true)
    try {
      const overrides: { prompt?: string; tone_override?: ToneOverride | null } = {}
      if (remixPrompt !== remixingItem.generation_params?.prompt) {
        overrides.prompt = remixPrompt
      }
      if (remixTone !== remixingItem.generation_params?.tone_override) {
        overrides.tone_override = remixTone
      }

      const response = await api.remixContent(remixingItem.id, overrides)
      isRemixJobRef.current = true
      startJob(response.job_id)
      setRemixingItem(null)
      addToast('Remixing content...', 'success')
    } catch (err) {
      if (err instanceof ApiError && err.code === ErrorCode.GENERATION_LIMIT_REACHED) {
        setRemixingItem(null)
        setShowPaywall(true)
      } else {
        addToast(err instanceof ApiError ? getErrorMessage(err, 'Failed to remix') : 'Failed to remix', 'error')
      }
    } finally {
      setIsRemixing(false)
    }
  }

  const handleSchedule = async (scheduledFor: string) => {
    if (!schedulingItem) return
    setIsScheduling(true)
    try {
      await api.scheduleContent(schedulingItem.id, scheduledFor)
      addToast('Post scheduled!', 'success', {
        action: { label: 'View Scheduled', onClick: () => setActiveTab('scheduled') },
      })
      setSchedulingItem(null)
      loadQueue()
    } catch (err) {
      addToast(err instanceof ApiError ? getErrorMessage(err, 'Failed to schedule') : 'Failed to schedule', 'error')
    } finally {
      setIsScheduling(false)
    }
  }

  const handleCancelSchedule = async (item: ContentQueueItem) => {
    try {
      await api.cancelSchedule(item.id)
      addToast('Schedule cancelled', 'success', {
        action: { label: 'View Approved', onClick: () => setActiveTab('approved') },
      })
      loadQueue()
    } catch (err) {
      addToast(err instanceof ApiError ? getErrorMessage(err, 'Failed to cancel schedule') : 'Failed to cancel schedule', 'error')
    }
  }

  const handleConfirmSchedule = async (scheduledFor: string) => {
    if (!schedulingItem) return
    if (isReschedule) {
      setIsScheduling(true)
      try {
        await api.cancelSchedule(schedulingItem.id)
        await api.scheduleContent(schedulingItem.id, scheduledFor)
        addToast('Post rescheduled!', 'success', {
          action: { label: 'View Scheduled', onClick: () => setActiveTab('scheduled') },
        })
        setSchedulingItem(null)
        loadQueue()
      } catch (err) {
        addToast(err instanceof ApiError ? getErrorMessage(err, 'Failed to reschedule') : 'Failed to reschedule', 'error')
        loadQueue()
      } finally {
        setIsScheduling(false)
      }
    } else {
      await handleSchedule(scheduledFor)
    }
  }

  const getCurrentItems = () => {
    switch (activeTab) {
      case 'drafts':
        return drafts
      case 'approved':
        return approved
      case 'scheduled':
        return scheduled
      case 'posted':
        return posted
      default:
        return []
    }
  }

  const items = getCurrentItems()

  return (
    <PageContainer
      title="Content Queue"
      actions={
        <Link to="/create">
          <Button>Create New</Button>
        </Link>
      }
    >
      <Tabs tabs={statusTabs} activeTab={activeTab} onTabChange={setActiveTab} />

      <div className="mt-6">
        {jobIsGenerating && (
          <Card className="mb-4">
            <div className="flex items-center gap-4">
              <Spinner size="md" />
              <p className="font-medium">Generating your content...</p>
            </div>
          </Card>
        )}

        {isLoading ? (
          <div className="flex justify-center py-12">
            <Spinner size="lg" />
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-secondary-text mb-4">
              {activeTab === 'drafts' && 'No drafts yet. Generate some content!'}
              {activeTab === 'approved' && 'No approved content. Approve drafts to see them here.'}
              {activeTab === 'scheduled' && 'No scheduled posts. Schedule approved content to see it here.'}
              {activeTab === 'posted' && 'No posted content yet.'}
            </p>
            {activeTab === 'drafts' && (
              <Link to="/create">
                <Button>Generate Content</Button>
              </Link>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {items.map((item) => (
              <Card key={item.id}>
                <AccountIndicator account={item.account} />
                <div className="flex justify-between items-start gap-4">
                  <div className="flex-1 min-w-0">
                    {item.posted_at && (
                      <p className="text-xs text-secondary-text mb-2">
                        Posted {format(new Date(item.posted_at), 'MMM d, yyyy h:mm a')}
                      </p>
                    )}
                    {item.scheduled_for && activeTab === 'scheduled' && (
                      <p className="text-xs text-secondary-text mb-2">
                        Scheduled for {format(new Date(item.scheduled_for), 'EEE, MMM d, yyyy h:mm a')}
                      </p>
                    )}

                    <p className="text-sm whitespace-pre-wrap">{item.content_text}</p>

                    {item.media_urls && item.media_urls.length > 0 && (
                      <p className="text-xs text-secondary-text mt-1">
                        {item.media_urls.length} image{item.media_urls.length > 1 ? 's' : ''} attached
                      </p>
                    )}

                    {item.generation_params && (
                      <div className="bg-secondary-bg p-3 mt-2 space-y-2">
                        <div className="flex flex-wrap gap-1.5">
                          <Badge variant="outline">
                            {CONTENT_TYPE_LABELS[item.generation_params.content_type] ?? item.generation_params.content_type}
                          </Badge>
                          {item.generation_params.tone_override && (
                            <Badge variant="outline">
                              {TONE_LABELS[item.generation_params.tone_override] ?? item.generation_params.tone_override}
                            </Badge>
                          )}
                          {item.generation_params.use_voice && (
                            <Badge variant="outline">Voice</Badge>
                          )}
                          {item.generation_params.context && (
                            <Badge variant="outline">
                              {item.generation_params.context.type === 'url' ? 'URL context' : 'Text context'}
                            </Badge>
                          )}
                        </div>
                        {item.generation_params.context && (
                          item.generation_params.context.type === 'url' ? (
                            <a
                              href={item.generation_params.context.value}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="block text-xs text-secondary-text hover:text-primary-text truncate"
                            >
                              {item.generation_params.context.value}
                            </a>
                          ) : (
                            <p className="text-xs text-secondary-text truncate">
                              {item.generation_params.context.value}
                            </p>
                          )
                        )}
                        <p className="text-xs text-secondary-text truncate">
                          {item.generation_params.prompt}
                        </p>
                      </div>
                    )}

                    {(() => {
                      const replyId = getReplyToPlatformId(item)
                      const replyUrl = replyId ? getReplyUrl(getItemPlatform(item), replyId) : null
                      return replyId && replyUrl ? (
                        <a
                          href={replyUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-block text-xs text-secondary-text hover:text-primary-text mt-2"
                        >
                          In reply to post &rarr;
                        </a>
                      ) : null
                    })()}

                    <p className="text-xs text-secondary-text mt-2">
                      Created {format(new Date(item.created_at), 'MMM d, yyyy h:mm a')}
                    </p>
                  </div>

                  <div className="flex flex-col gap-2">
                    {activeTab === 'drafts' && (
                      <>
                        <Button variant="secondary" onClick={() => openEditModal(item)}>
                          Edit
                        </Button>
                        <Button onClick={() => handleUpdateStatus(item, 'approved')}>
                          Approve
                        </Button>
                        {item.generation_params && (
                          <Button variant="secondary" onClick={() => openRemixModal(item)}>
                            Remix
                          </Button>
                        )}
                        <Button variant="danger" onClick={() => setDeleteConfirmId(item.id)}>
                          Delete
                        </Button>
                      </>
                    )}
                    {activeTab === 'approved' && (
                      <>
                        {(() => {
                          const itemPlatform = getItemPlatform(item)
                          const config = getPlatformConfig(itemPlatform)
                          const needsMedia = config.mediaRequired && (!item.media_urls || item.media_urls.length === 0)
                          return (
                            <Button
                              onClick={() => handlePost(item)}
                              disabled={needsMedia}
                              title={needsMedia ? `Add an image to post to ${config.displayName}` : undefined}
                            >
                              Post Now
                            </Button>
                          )
                        })()}
                        <Button variant="secondary" onClick={() => { setSchedulingItem(item); setIsReschedule(false) }}>
                          Post Later
                        </Button>
                        <Button variant="secondary" onClick={() => openEditModal(item)}>
                          Edit
                        </Button>
                        {item.generation_params && (
                          <Button variant="secondary" onClick={() => openRemixModal(item)}>
                            Remix
                          </Button>
                        )}
                        <Button
                          variant="secondary"
                          onClick={() => handleUpdateStatus(item, 'draft')}
                        >
                          Move to Drafts
                        </Button>
                      </>
                    )}
                    {activeTab === 'scheduled' && (
                      <>
                        <Button variant="secondary" onClick={() => { setSchedulingItem(item); setIsReschedule(true) }}>
                          Reschedule
                        </Button>
                        <Button variant="secondary" onClick={() => handleCancelSchedule(item)}>
                          Cancel
                        </Button>
                      </>
                    )}
                    {activeTab === 'posted' && (() => {
                      const viewUrl = getItemPostUrl(item)
                      const platformName = getPlatformDisplayName(getItemPlatform(item))
                      return viewUrl ? (
                        <Button
                          variant="secondary"
                          onClick={() => window.open(viewUrl, '_blank')}
                        >
                          View on {platformName}
                        </Button>
                      ) : null
                    })()}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Edit Modal */}
      <Modal
        isOpen={!!editingItem}
        onClose={() => setEditingItem(null)}
        title="Edit Content"
        actions={
          <>
            <Button variant="secondary" onClick={() => setEditingItem(null)}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveEdit}
              disabled={editingItem ? editedContent.length > getCharLimit(getItemPlatform(editingItem)) : false}
            >
              Save
            </Button>
          </>
        }
      >
        {editingItem && (() => {
          const editPlatform = getItemPlatform(editingItem)
          const charLimit = getCharLimit(editPlatform)
          const config = getPlatformConfig(editPlatform)
          return (
            <div className="space-y-4">
              <TextArea
                value={editedContent}
                onChange={(e) => setEditedContent(e.target.value)}
                charCount={editedContent.length}
                maxChars={charLimit}
                rows={4}
              />
              {config.supportsMedia && (
                <MediaUpload
                  mediaUrls={editMediaUrls}
                  onMediaChange={setEditMediaUrls}
                  required={config.mediaRequired}
                  maxFiles={editPlatform === 'instagram' ? 10 : 4}
                  onError={(msg) => {
                    setEditMediaError(msg)
                    addToast(msg, 'error')
                  }}
                />
              )}
              {editMediaError && (
                <p className="text-xs text-error">{editMediaError}</p>
              )}
            </div>
          )
        })()}
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={deleteConfirmId !== null}
        onClose={() => setDeleteConfirmId(null)}
        title="Delete Content"
        actions={
          <>
            <Button variant="secondary" onClick={() => setDeleteConfirmId(null)}>
              Cancel
            </Button>
            <Button variant="danger" onClick={() => deleteConfirmId && handleDelete(deleteConfirmId)}>
              Delete
            </Button>
          </>
        }
      >
        <p>Are you sure you want to delete this content? This action cannot be undone.</p>
      </Modal>

      {/* Remix Modal */}
      <Modal
        isOpen={!!remixingItem}
        onClose={() => setRemixingItem(null)}
        title="Remix Content"
        actions={
          <>
            <Button variant="secondary" onClick={() => setRemixingItem(null)}>
              Cancel
            </Button>
            <Button onClick={handleRemix} loading={isRemixing} disabled={isRemixing}>
              Remix
            </Button>
          </>
        }
      >
        {remixingItem?.generation_params && (
          <div className="space-y-4">
            <AccountIndicator account={remixingItem.account} />
            <div className="flex flex-wrap gap-1.5">
              <Badge variant="outline">
                {CONTENT_TYPE_LABELS[remixingItem.generation_params.content_type] ?? remixingItem.generation_params.content_type}
              </Badge>
              {remixingItem.generation_params.use_voice && (
                <Badge variant="outline">Voice</Badge>
              )}
              {remixingItem.generation_params.context && (
                <Badge variant="outline">
                  {remixingItem.generation_params.context.type === 'url' ? 'URL context' : 'Text context'}
                </Badge>
              )}
            </div>
            {remixingItem.generation_params.context && (
              remixingItem.generation_params.context.type === 'url' ? (
                <a
                  href={remixingItem.generation_params.context.value}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block text-xs text-secondary-text hover:text-primary-text truncate"
                >
                  {remixingItem.generation_params.context.value}
                </a>
              ) : (
                <p className="text-xs text-secondary-text truncate">
                  {remixingItem.generation_params.context.value}
                </p>
              )
            )}

            <div>
              <label className="block text-sm font-medium mb-1">Prompt</label>
              <TextArea
                value={remixPrompt}
                onChange={(e) => setRemixPrompt(e.target.value)}
                rows={3}
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Tone</label>
              <RadioGroup
                options={TONE_OPTIONS}
                value={remixTone}
                onChange={(v) => setRemixTone(v as ToneOverride)}
                name="remix-tone"
              />
            </div>

            <p className="text-xs text-secondary-text">
              This will create a new draft with the same settings but a fresh generation.
            </p>
          </div>
        )}
      </Modal>

      <ScheduleModal
        isOpen={!!schedulingItem}
        onClose={() => setSchedulingItem(null)}
        onConfirm={handleConfirmSchedule}
        isLoading={isScheduling}
        title={isReschedule ? 'Reschedule Post' : 'Schedule Post'}
      />

      <PaywallModal isOpen={showPaywall} onClose={() => setShowPaywall(false)} />

      <ToastContainer toasts={toasts} onDismiss={dismissToast} />
    </PageContainer>
  )
}
